import { useState, type FormEvent } from 'react'
import { createStaffRequestSchema, STAFF_NAME_MAX, type StaffInvite } from '@manaro/contracts'
import { ApiError } from '@/shared/api/client.ts'
import { Button } from '@/shared/ui/button.tsx'
import { Field } from '@/shared/ui/field.tsx'
import { Alert } from '@/shared/ui/states.tsx'
import { useCreateStaff } from '../use-staff.ts'
import { StaffDialog } from './staff-dialog.tsx'

interface AddStaffDialogProps {
  onCreated: (invite: StaffInvite) => void
  onClose: () => void
}

export function AddStaffDialog({ onCreated, onClose }: AddStaffDialogProps) {
  const [email, setEmail] = useState('')
  const [name, setName] = useState('')
  const [validationError, setValidationError] = useState<string | null>(null)
  const create = useCreateStaff()

  const error =
    validationError ??
    (create.error ? (create.error instanceof ApiError ? create.error.message : 'Не получилось — попробуйте ещё раз.') : null)

  return (
    <StaffDialog titleId="add-staff-title" busy={create.isPending} onClose={onClose}>
      {(close) => {
        const submit = (event: FormEvent) => {
          event.preventDefault()
          if (create.isPending) return
          const parsed = createStaffRequestSchema.safeParse({ email, name })
          if (!parsed.success) {
            setValidationError(parsed.error.issues[0]?.message ?? 'Проверьте почту и имя.')
            return
          }
          setValidationError(null)
          create.mutate(parsed.data, { onSuccess: onCreated })
        }

        return (
          <form onSubmit={submit} className="flex flex-col gap-4 px-6 py-5.5" noValidate>
            <div className="flex flex-col gap-1.5">
              <h2 id="add-staff-title" className="font-display text-[19px] font-semibold tracking-[-0.01em]">
                Добавить сотрудника поддержки
              </h2>
              <p className="text-[14px] text-ink-muted text-balance-pretty">
                Он увидит чаты, историю обращений, салоны и обновления. Выручка, рефералы и доступы останутся закрыты.
              </p>
            </div>

            <Field
              label="Рабочая почта"
              type="email"
              autoComplete="off"
              placeholder="nastya@manaro.io"
              value={email}
              autoFocus
              onChange={(event) => {
                setEmail(event.target.value)
                setValidationError(null)
              }}
            />
            <Field
              label="Имя"
              autoComplete="off"
              placeholder="Анастасия"
              maxLength={STAFF_NAME_MAX}
              value={name}
              onChange={(event) => {
                setName(event.target.value)
                setValidationError(null)
              }}
              action={<span className="text-[12px] text-ink-faint">видно клиентам в чате</span>}
            />

            {error && <Alert>{error}</Alert>}

            <div className="flex flex-wrap justify-end gap-2">
              <Button variant="secondary" size="sm" disabled={create.isPending} onClick={close}>
                Отмена
              </Button>
              <Button type="submit" size="sm" loading={create.isPending}>
                Получить ссылку
              </Button>
            </div>
          </form>
        )
      }}
    </StaffDialog>
  )
}
